/**
 * Step 3: Creative — status summary + AI generation panel (top) + country-tab manual pickers (bottom).
 */

import { Sparkles, Hand, Minus } from 'lucide-react';

import { useAuth } from '@/providers/AuthProvider';
import { countryToFlagEmoji } from '@/utils/country-flag-emoji';
import type { DropLabel } from '@/features/campaign-texts/components/PerDropGroupSelector';

import type { useMultiCountryBulkEditForm } from '../hooks';
import type { useMultiCountryCreative } from '../hooks/useMultiCountryCreative';
import type { useMultiCountrySentScheduledEditForm } from '@/features/smswiser-campaigns/components/MultiCountrySentScheduledEdit/hooks/useMultiCountrySentScheduledEditForm';
import { AiGenerationPanel, CountryManualPickers } from '../creative';

type Form =
  | ReturnType<typeof useMultiCountryBulkEditForm>
  | ReturnType<typeof useMultiCountrySentScheduledEditForm>;
type Creative = ReturnType<typeof useMultiCountryCreative>;

interface Props {
  form: Form;
  creative: Creative;
  countryKeys: string[];
  dropLabels: DropLabel[];
  dropLabelsByCountry: Record<string, DropLabel[]>;
  sendersList: string[];
  textsList: string[];
  isBusy: boolean;
}

export function CreativeStep({ form, creative, countryKeys, dropLabels, dropLabelsByCountry, sendersList, textsList, isBusy }: Props) {
  const { hasPermission } = useAuth();
  const showAi = hasPermission('campaign_texts.generate');
  const showAiSenders = hasPermission('campaign_senders.generate');

  return (
    <div className="flex flex-col gap-5 p-5">
      <CreativeStatusSummary form={form} creative={creative} countryKeys={countryKeys} />
      <AiGenerationPanel
        creative={creative} dropLabels={dropLabels} dropLabelsByCountry={dropLabelsByCountry}
        showAi={showAi} showAiSenders={showAiSenders}
      />
      <CountryManualPickers
        form={form} countryKeys={countryKeys}
        sendersList={sendersList} textsList={textsList} isBusy={isBusy}
      />
    </div>
  );
}

function CreativeStatusSummary({ form, creative, countryKeys }: {
  form: Form;
  creative: Creative;
  countryKeys: string[];
}) {
  if (countryKeys.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border/60 bg-muted/20 px-4 py-2.5">
      <span className="text-xs font-medium text-muted-foreground">Status:</span>
      {countryKeys.map((ck) => {
        const hasAi = (creative.resultsByCountry[ck]?.texts.length ?? 0) > 0 ||
          (creative.resultsByCountry[ck]?.senders.length ?? 0) > 0;
        const hasManual = (form.perCountryTexts[ck]?.length ?? 0) > 0 ||
          (form.perCountrySenders[ck]?.length ?? 0) > 0;
        return (
          <span key={ck} className="flex items-center gap-1 rounded-md border border-border/40 bg-card px-2 py-1 text-xs">
            <span>{countryToFlagEmoji(ck)}</span>
            <span className="font-medium">{ck}</span>
            {hasManual ? (
              <span className="flex items-center gap-0.5 text-amber-600"><Hand className="h-3 w-3" /> Manual</span>
            ) : hasAi ? (
              <span className="flex items-center gap-0.5 text-primary"><Sparkles className="h-3 w-3" /> AI</span>
            ) : (
              <span className="flex items-center gap-0.5 text-muted-foreground"><Minus className="h-3 w-3" /> None</span>
            )}
          </span>
        );
      })}
    </div>
  );
}
